/**
 * Properties — top-level property list (Module 4, Phase 1, AD-013).
 *
 * A Property is the permanent, reusable store for a physical address — the
 * "Premises Data Hub". This screen is the entry point: one row per property,
 * search bar, "+ New property" drawer, and a toggle for archived properties.
 *
 * Pattern: matches the Contacts.tsx list-screen shape (ListScreen + Card rows).
 *
 * Phase 1 corrections (2026-06-18):
 *   · Single comma-separated coordinate box (decimal OR DMS, mirrors StaffTechnicalPanel).
 *   · Address auto-fetched via Nominatim reverse geocoding when coords are entered.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useToast } from '../components/Toast';
import { Box, Btn3, EmptyNote, ListScreen, Tag } from '../components/ui';
import { ListLoading } from '../components/ListState';
import {
  createProperty,
  listenAllProperties,
  listenProperties,
} from '../lib/propertyStore';
import { linkPropertyAsCandidate } from '../lib/linkPropertyAsCandidate';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import {
  PROJECT_TYPE_LABELS,
  propertyCityLabel,
  type LiveProperty,
  type ProjectType,
  type PropertySeed,
} from '../lib/propertyModel';
import { parseCoordsString, reverseGeocode } from '../lib/geocode';

// ─── Create form ──────────────────────────────────────────────────────────
// OWNER REDESIGN (2026-07-03): the old form was hand-rolled 11–12px labels on
// bare unstyled <input>s (the `ipt` class it referenced exists nowhere) —
// "boxes so tiny it's very difficult to understand". Rebuilt on the portal's
// canonical field idiom (the StaffTechnicalPanel `field`/`lbl` + IntakeInputs
// `inputStyle` shape: rule-strong border · r-sm radius · surface bg · ink
// text), sized up to 14px text + comfortable padding for the owner-facing
// create flow, in clearly-titled sections with plain-language helper text.
// Tokens only — no raw hex.

const field: React.CSSProperties = {
  border: '1px solid var(--rule-strong)',
  borderRadius: 'var(--r-sm)',
  padding: '10px 12px',
  fontSize: 14,
  fontFamily: 'inherit',
  color: 'var(--ink)',
  background: 'var(--surface)',
  width: '100%',
};
const fLbl: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--ink)',
  margin: '0 0 4px',
};
const fHint: React.CSSProperties = {
  fontSize: 12.5,
  color: 'var(--ink-mute)',
  lineHeight: 1.55,
  margin: '5px 0 0',
};
const fSectionTitle: React.CSSProperties = {
  fontSize: 14.5,
  fontWeight: 700,
  color: 'var(--ink)',
  margin: '0 0 2px',
};
const fSectionSub: React.CSSProperties = {
  fontSize: 12.5,
  color: 'var(--ink-mute)',
  lineHeight: 1.55,
  margin: '0 0 12px',
};
/** A titled form section; sections after the first carry a dashed divider
 *  (the StaffTechnicalPanel section idiom). Normal page flow — no inner
 *  scrollbox, ever (owner hard rule: one page scroll). */
function FormSection({
  title,
  sub,
  first,
  children,
}: {
  title: string;
  sub: string;
  first?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      style={
        first
          ? undefined
          : { marginTop: 18, paddingTop: 16, borderTop: '1px dashed var(--rule)' }
      }
    >
      <div style={fSectionTitle}>{title}</div>
      <p style={fSectionSub}>{sub}</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>{children}</div>
    </div>
  );
}

function CreatePropertyForm({
  onCreated,
  onCancel,
}: {
  onCreated: (pid: string) => void;
  onCancel: () => void;
}) {
  const { user } = useAuth();
  const { toast } = useToast();

  // Address — auto-fetched from Nominatim when coords are resolved, editable
  const [address, setAddress] = useState('');
  const [fetchingAddress, setFetchingAddress] = useState(false);

  // Google Maps URL
  const [mapsUrl, setMapsUrl] = useState('');

  // Combined coords box (decimal or DMS, single string)
  const [coordsRaw, setCoordsRaw] = useState('');

  // Meta
  const [projectType, setProjectType] = useState<ProjectType | ''>('');
  const [northRotation, setNorthRotation] = useState('');
  const [busy, setBusy] = useState(false);

  // Mirror coords extracted from mapsUrl into coordsRaw when pasted
  // (same pattern as StaffTechnicalPanel: link→field mirror)
  useEffect(() => {
    if (!mapsUrl.trim()) return;
    const parsed = parseCoordsString(mapsUrl);
    if (parsed) {
      const canon = `${parsed.lat}, ${parsed.lng}`;
      if (canon !== coordsRaw) setCoordsRaw(canon);
    }
  // Only re-run when mapsUrl changes, not coordsRaw
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapsUrl]);

  const parsedCoords = useMemo(() => parseCoordsString(coordsRaw), [coordsRaw]);
  const coordsInvalid = coordsRaw.trim() !== '' && parsedCoords == null;

  // Auto-fetch address on coords blur
  async function onCoordsBlur() {
    if (!parsedCoords || address) return;
    setFetchingAddress(true);
    try {
      const fetched = await reverseGeocode(parsedCoords.lat, parsedCoords.lng);
      if (fetched && !address) setAddress(fetched);
    } finally {
      setFetchingAddress(false);
    }
  }

  async function handleCreate() {
    const hasGoogle = Boolean(mapsUrl.trim());
    const hasCoords = parsedCoords != null;

    if (!hasGoogle && !hasCoords) {
      toast('Google Maps location or coordinates are required.');
      return;
    }

    // Build siteLocation
    let siteLocation: PropertySeed['siteLocation'] = undefined;
    if (hasGoogle || hasCoords) {
      siteLocation = {};
      if (mapsUrl.trim()) siteLocation.mapsUrl = mapsUrl.trim();
      if (parsedCoords) {
        siteLocation.lat = parsedCoords.lat;
        siteLocation.lng = parsedCoords.lng;
        siteLocation.rawCoords = coordsRaw.trim();
        siteLocation.coordFormat = 'decimal';
      }
    }

    setBusy(true);
    try {
      const pid = await createProperty(
        {
          address: address.trim() || undefined,
          siteLocation,
          projectType: projectType || undefined,
          northRotation: northRotation ? Number(northRotation) : undefined,
        },
        user?.uid ?? 'unknown',
      );
      toast(`Property ${pid} created.`, { ok: true });
      onCreated(pid);
    } catch (err) {
      console.error('createProperty failed', err);
      toast('Could not create property. Please try again.');
      setBusy(false);
    }
  }

  // Create unlocks once the ONE required thing — the location — is given
  // either way (a Maps link OR parseable coordinates). Same rule handleCreate
  // enforces; surfacing it on the button means no dead-feeling click + toast.
  const canCreate = (mapsUrl.trim() !== '' || parsedCoords != null) && !coordsInvalid;

  return (
    <div data-testid="create-property-form">
      {/* ══ 1 · WHERE IS THE PROPERTY? — the one required section ══ */}
      <FormSection
        first
        title="Where is the property?"
        sub="Give the location either way — paste a Google Maps link OR type the
             coordinates. One of the two is required; from it the address below
             is fetched automatically and the site can be opened on a map in
             one click."
      >
        <div>
          <label htmlFor="prop-maps-url" style={fLbl}>
            Google Maps link{' '}
            <span style={{ color: 'var(--crit)' }} aria-hidden>
              *
            </span>
          </label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              id="prop-maps-url"
              type="url"
              placeholder="Paste the full maps.google.com link"
              value={mapsUrl}
              onChange={(e) => setMapsUrl(e.target.value)}
              style={{ ...field, flex: 1 }}
              autoFocus
            />
            {mapsUrl.trim() && (
              <a
                href={mapsUrl.trim()}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--accent)',
                  whiteSpace: 'nowrap',
                }}
              >
                Open ↗
              </a>
            )}
          </div>
          <p style={fHint}>
            In Google Maps, find the plot, then copy the link from the address
            bar. If the link carries the pin's coordinates they are filled into
            the box below for you.
          </p>
        </div>

        <div>
          <label htmlFor="prop-coords" style={fLbl}>
            Coordinates (latitude, longitude){' '}
            <span style={{ color: 'var(--crit)' }} aria-hidden>
              *
            </span>{' '}
            <span style={{ fontWeight: 400, color: 'var(--ink-mute)' }}>
              — needed only if there is no Maps link
            </span>
          </label>
          <input
            id="prop-coords"
            type="text"
            placeholder="e.g. 21.2345, 81.6789 — or DMS like 21°14'04″N 81°40'44″E"
            value={coordsRaw}
            onChange={(e) => setCoordsRaw(e.target.value)}
            onBlur={onCoordsBlur}
            style={field}
          />
          <p style={fHint}>
            Copy “latitude, longitude” straight from Google Maps (right-click
            the pin). Decimal or DMS both work; use a minus sign for
            south/west.
            {coordsInvalid && (
              <span style={{ color: 'var(--crit)', fontWeight: 600 }}>
                {' '}
                Could not read these — use “lat, long” decimal or DMS with
                N/S/E/W.
              </span>
            )}
          </p>
        </div>
      </FormSection>

      {/* ══ 2 · ADDRESS — auto-fetched, always editable ══ */}
      <FormSection
        title="Address"
        sub="Fetched automatically from the location above (OpenStreetMap) the
             moment the coordinates are in — check it and correct anything.
             This exact text is what reports and project pages print for the
             property."
      >
        <div>
          <label htmlFor="prop-address" style={fLbl}>
            Full property address
          </label>
          <textarea
            id="prop-address"
            rows={3}
            placeholder={
              fetchingAddress
                ? 'Fetching the address from the coordinates…'
                : 'House / plot, street, locality, city, state, PIN'
            }
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            disabled={fetchingAddress}
            style={{ ...field, resize: 'vertical', minHeight: 84, lineHeight: 1.5 }}
          />
          {fetchingAddress && (
            <p style={fHint} aria-live="polite">
              Fetching the address from the coordinates…
            </p>
          )}
        </div>
      </FormSection>

      {/* ══ 3 · PROPERTY DETAILS — optional, can be added later ══ */}
      <FormSection
        title="Property details"
        sub="Both optional — they can be filled in (or corrected) any time on
             the property's own page."
      >
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 220px', minWidth: 200 }}>
            <label htmlFor="prop-type" style={fLbl}>
              Project type
            </label>
            <select
              id="prop-type"
              value={projectType}
              onChange={(e) => setProjectType(e.target.value as ProjectType | '')}
              style={field}
            >
              <option value="">— select —</option>
              {(Object.entries(PROJECT_TYPE_LABELS) as [ProjectType, string][]).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
            <p style={fHint}>
              What kind of premises this is — home, plot, office… It picks the
              right checklists for the evaluation later.
            </p>
          </div>
          <div style={{ flex: '1 1 180px', minWidth: 160 }}>
            <label htmlFor="prop-north" style={fLbl}>
              North bearing (°)
            </label>
            <input
              id="prop-north"
              type="number"
              min={0}
              max={359.9}
              step={0.1}
              placeholder="0 – 359.9"
              value={northRotation}
              onChange={(e) => setNorthRotation(e.target.value)}
              style={field}
            />
            <p style={fHint}>
              From the site survey or Google Earth: which way true north sits
              relative to the plan. 0° means the plan is already aligned to
              true north. The Vastu grid is drawn from this.
            </p>
          </div>
        </div>
      </FormSection>

      {/* ══ Footer — ONE Cancel (here, nowhere else) + Create ══ */}
      <div
        style={{
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          marginTop: 20,
          paddingTop: 14,
          borderTop: '1px solid var(--rule)',
        }}
      >
        {!canCreate && (
          <span style={{ fontSize: 12.5, color: 'var(--ink-mute)', marginRight: 'auto' }}>
            Add the location above (Maps link or coordinates) to enable Create.
          </span>
        )}
        <Btn3 intent="ghost" size="sm" onClick={onCancel} disabled={busy}>
          Cancel
        </Btn3>
        <Btn3 intent="grn" size="sm" onClick={handleCreate} disabled={busy || !canCreate}>
          {busy ? 'Creating…' : 'Create property'}
        </Btn3>
      </div>
    </div>
  );
}

// ─── Property row ─────────────────────────────────────────────────────────

function PropertyRow({ prop }: { prop: LiveProperty }) {
  const navigate = useNavigate();
  const city = propertyCityLabel(prop);

  const primaryLabel = prop.address
    || (prop.siteLocation?.lat != null
        ? `${prop.siteLocation.lat.toFixed(6)}, ${prop.siteLocation.lng?.toFixed(6)}`
        : prop.siteLocation?.mapsUrl
        ? 'Google Maps pin'
        : '(no address)');

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: row is supplementary nav
    <div
      className="card row-card"
      style={{ cursor: 'pointer' }}
      onClick={() => navigate(`/properties/${prop.propertyId}`)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/properties/${prop.propertyId}`); }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {/* Property ID chip */}
        <span
          style={{
            fontFamily: 'monospace',
            fontSize: 11,
            background: '#eef4fb',
            color: '#2f5d8a',
            borderRadius: 4,
            padding: '2px 6px',
            flexShrink: 0,
          }}
        >
          {prop.propertyId}
        </span>

        {/* Address / location label */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: 13,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {primaryLabel}
          </div>
          {city && (
            <div style={{ fontSize: 11, color: 'var(--ink-mute)', marginTop: 1 }}>{city}</div>
          )}
        </div>

        {/* Badges */}
        <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap', alignItems: 'center' }}>
          {prop.projectType && (
            <Tag kind="val">
              {PROJECT_TYPE_LABELS[prop.projectType] ?? prop.projectType}
            </Tag>
          )}
          {prop.engagementRefs.length > 0 && (
            <Tag kind="default">
              {prop.engagementRefs.length === 1
                ? '1 project'
                : `${prop.engagementRefs.length} projects`}
            </Tag>
          )}
          {prop.northRotation !== undefined && (
            <Tag kind="default">{prop.northRotation}°N</Tag>
          )}
          {prop.archived && <Tag kind="warn">Archived</Tag>}
        </div>
      </div>
    </div>
  );
}

// ─── Main screen ──────────────────────────────────────────────────────────

export default function Properties() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const linkTo = searchParams.get('linkTo') ?? '';

  const [props, setProps] = useState<LiveProperty[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    const unsub = showArchived
      ? listenAllProperties(setProps)
      : listenProperties(setProps);
    return unsub;
  }, [showArchived]);

  const filtered = useMemo(() => {
    if (!props) return null;
    const q = query.trim().toLowerCase();
    if (!q) return props;
    return props.filter(
      (p) =>
        (p.address ?? '').toLowerCase().includes(q) ||
        p.propertyId.toLowerCase().includes(q) ||
        (p.addressComps?.city ?? '').toLowerCase().includes(q) ||
        (p.siteLocation?.mapsUrl ?? '').toLowerCase().includes(q),
    );
  }, [props, query]);

  async function handleCreated(pid: string) {
    setCreating(false);
    if (linkTo) {
      // Determine the role based on the project's tier.
      try {
        const projSnap = await getDoc(doc(db, 'projects', linkTo));
        const data = projSnap.exists()
          ? (projSnap.data() as Record<string, unknown>)
          : {};
        const bareTier = ((data.tierCode as string | undefined) || '')
          .split(/\s*[·•]/)[0]
          .trim()
          .toUpperCase();
        const isL1A = bareTier === 'L1A';
        const role: 'primary' | 'candidate' = isL1A ? 'candidate' : 'primary';
        const projClientId = (data.clientId as string | undefined) || '';
        const intakeTier = bareTier ? `${bareTier}_R1` : '';
        // ONE unified link path (Gap #2): writes both refs + (L1A) ensures a
        // pre-stamped candidate so the new property is gradeable in the project.
        await linkPropertyAsCandidate(linkTo, projClientId, pid, role, intakeTier, {
          ensureCandidate: isL1A && !!projClientId,
        });
      } catch (e) {
        console.warn('[Properties] linkTo auto-link failed (non-fatal):', (e as Error).message);
      }
      navigate(`/project/${linkTo}`);
      return;
    }
    navigate(`/properties/${pid}`);
  }

  // ── EXCLUSIVE CREATE VIEW (owner redesign 2026-07-03) ──────────────────
  // While creating, ONLY the form renders — no search bar, no archived
  // toggle, no property list rendering below it ("confusing and
  // space-stealing"). Deliberately NOT inside the ListScreen chrome: its
  // `.list-formslot` sits in a viewport-locked flex box whose only scroller
  // is the row area, so a tall form there would clip on short windows.
  // Normal page flow instead = ONE page scroll (owner hard rule — no inner
  // scrollbox). Create-success and Cancel both return to the list view;
  // ?linkTo= deep-link behaviour is unchanged (handleCreated).
  if (creating) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <Box
          title="New property"
          note="The permanent record of a physical premises — reused by every project at that address"
        >
          <CreatePropertyForm
            onCreated={handleCreated}
            onCancel={() => setCreating(false)}
          />
        </Box>
      </div>
    );
  }

  const toolbar = (
    <>
      <input
        type="search"
        placeholder="Search properties…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        style={{ ...field, flex: 1, minWidth: 160, width: undefined }}
        aria-label="Search properties"
      />
      <label
        style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5, cursor: 'pointer' }}
      >
        <input
          type="checkbox"
          checked={showArchived}
          onChange={(e) => setShowArchived(e.target.checked)}
        />
        Show archived
      </label>
      {/* Opens the EXCLUSIVE create view (never a toggle-to-Cancel — the ONE
          Cancel lives in the form footer). */}
      <Btn3 intent="ink" size="sm" onClick={() => setCreating(true)}>
        + New property
      </Btn3>
    </>
  );

  return (
    <ListScreen
      title="Properties"
      note="Physical premises — permanent records"
      toolbar={toolbar}
    >
      {filtered === null ? (
        <ListLoading />
      ) : filtered.length === 0 ? (
        query ? (
          <EmptyNote title="No matches">
            No properties match "{query}".
          </EmptyNote>
        ) : showArchived ? (
          <EmptyNote title="No properties">
            No properties exist yet.
          </EmptyNote>
        ) : (
          <EmptyNote title="No properties yet">
            Create one when working on a project.
          </EmptyNote>
        )
      ) : (
        filtered.map((p) => <PropertyRow key={p.propertyId} prop={p} />)
      )}
    </ListScreen>
  );
}
